import { Vector2 } from 'three';
import { Flow, ManualArea, RackingArea, Rectangle, SingularStationArea } from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { theme } from '@/modules/common/theme';
import { getCommonOption, getCommonString } from '@/modules/common/helpers/commonValue';
import { flowFactory } from '@/modules/floorplanDesign/factories';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { Area, AreaLoadHandlingType } from '@/modules/floorplanDesign/types';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import {
  getAreaLoadHandlingType,
  isLoadHandlingArea,
} from '@/modules/floorplanDesign/helpers/areaLoadHandling';

import {
  CONSOLIDATION_FLOW_LINE_COLOR,
  GOODS_TO_PERSONS_FLOW_LINE_COLOR,
  EXTERNAL_FLOW_LINE_COLOR,
  TRANSPORT_FLOW_LINE_COLOR,
} from './consts';
import { logger } from '@common/logging/logger';

type processRelatedFlowMap = Map<
  string,
  { processAreaId: string; inbound: Flow[]; outbound: Flow[]; processingTime: number }
>;

/**
 * Returns the base id of an endpoint.
 * Used for resolving canvas sub-endpoints like "Shuttle-1/Storage" back to "Shuttle-1".
 */
export const getEndpointBaseId = (endpointId: string): string => {
  const separatorIndex = endpointId.indexOf('/');
  return separatorIndex === -1 ? endpointId : endpointId.slice(0, separatorIndex);
};

export const createProcessRelatedFlowsMap = (
  ids: string[],
  flows: Flow[],
): processRelatedFlowMap => {
  const processAreaIds = new Set(ids);
  const processRelatedFlowMap: processRelatedFlowMap = new Map();

  flows.forEach((flow: Flow) => {
    if (processAreaIds.has(flow.destinationAreaOrFlowStopId)) {
      const processRelatedFlows = processRelatedFlowMap.get(flow.destinationAreaOrFlowStopId);
      if (processRelatedFlows) {
        processRelatedFlows.inbound.push(flow);
      } else {
        processRelatedFlowMap.set(flow.destinationAreaOrFlowStopId, {
          processAreaId: flow.destinationAreaOrFlowStopId,
          inbound: [flow],
          outbound: [],
          processingTime: 0,
        });
      }
    }

    if (processAreaIds.has(flow.sourceAreaOrFlowStopId)) {
      const processRelatedFlows = processRelatedFlowMap.get(flow.sourceAreaOrFlowStopId);
      if (processRelatedFlows) {
        processRelatedFlows.outbound.push(flow);
      } else {
        processRelatedFlowMap.set(flow.sourceAreaOrFlowStopId, {
          processAreaId: flow.sourceAreaOrFlowStopId,
          inbound: [],
          outbound: [flow],
          processingTime: 0,
        });
      }
    }
  });

  return processRelatedFlowMap;
};

export const getAreaAttachPoint = (rectangle: Rectangle, otherPoint?: Vector2): Vector2 => {
  const centerX = rectangle.centerX * 1000;
  const centerY = rectangle.centerY * 1000;
  const halfLengthMm = rectangle.length * 500;
  const halfWidthMm = rectangle.width * 500;

  if (!otherPoint) {
    return new Vector2(centerX, centerY);
  }

  const dx = otherPoint.x - centerX;
  const dy = otherPoint.y - centerY;

  if (dx === 0 && dy === 0) {
    return new Vector2(centerX, centerY);
  }

  const cos = Math.cos(rectangle.angle);
  const sin = Math.sin(rectangle.angle);

  const localX = dx * cos + dy * sin;
  const localY = -dx * sin + dy * cos;

  const tx = localX === 0 ? Infinity : halfLengthMm / Math.abs(localX);
  const ty = localY === 0 ? Infinity : halfWidthMm / Math.abs(localY);
  const t = Math.min(tx, ty);

  const exitLocalX = localX * t;
  const exitLocalY = localY * t;

  const worldX = centerX + exitLocalX * cos - exitLocalY * sin;
  const worldY = centerY + exitLocalX * sin + exitLocalY * cos;

  return new Vector2(worldX, worldY);
};

// TODO: rotated groups don't work
// TODO: areasInGroup can't contain highways
export const getGroupAttachPoint = (areasInGroup: Area[], otherPoint?: Vector2): Vector2 => {
  // Axis-aligned extents of each (possibly rotated) rectangle in the group.
  const extents = areasInGroup.flatMap((area) => {
    if (isHighwayArea(area)) return [];

    const halfLength = area.rectangle.length / 2;
    const halfWidth = area.rectangle.width / 2;
    const cos = Math.abs(Math.cos(area.rectangle.angle));
    const sin = Math.abs(Math.sin(area.rectangle.angle));
    const extentX = halfLength * cos + halfWidth * sin;
    const extentY = halfLength * sin + halfWidth * cos;

    return [
      {
        minX: area.rectangle.centerX - extentX,
        maxX: area.rectangle.centerX + extentX,
        minY: area.rectangle.centerY - extentY,
        maxY: area.rectangle.centerY + extentY,
      },
    ];
  });

  if (extents.length === 0) {
    return new Vector2(0, 0);
  }

  const minX = Math.min(...extents.map((e) => e.minX));
  const minY = Math.min(...extents.map((e) => e.minY));
  const maxX = Math.max(...extents.map((e) => e.maxX));
  const maxY = Math.max(...extents.map((e) => e.maxY));

  const boundingRectangle: Rectangle = {
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    length: maxX - minX,
    width: maxY - minY,
    angle: 0,
  };

  return getAreaAttachPoint(boundingRectangle, otherPoint);
};

export const inferConsolidationFlowType = (
  loadHandlingType: SingularStationArea['loadHandlingType'],
): Flow['type'] | undefined => {
  switch (loadHandlingType) {
    case 'STORAGE':
      return 'CONSOLIDATION_RETRIEVAL';
    case 'INTAKE':
    case 'OFF_VEHICLE_PROCESSING':
    case 'ON_VEHICLE_PROCESSING':
      return 'CONSOLIDATION_STORAGE';
    default:
      return undefined;
  }
};

export const inferFlowType = (
  tool: 'transport_flow' | 'consolidation_flow' | 'external_flow',
  loadHandlingType: SingularStationArea['loadHandlingType'],
): Flow['type'] | undefined => {
  switch (tool) {
    case 'consolidation_flow':
      return inferConsolidationFlowType(loadHandlingType);
    case 'transport_flow':
      return 'TRANSPORT';
    case 'external_flow':
      return 'EXTERNAL';
    default:
      assertNever(tool);
  }
};

export const convertFlowTypeToToolFlowType = (
  flowType: Flow['type'],
): 'transport_flow' | 'consolidation_flow' | 'external_flow' | undefined => {
  switch (flowType) {
    case 'TRANSPORT':
      return 'transport_flow';
    case 'CONSOLIDATION_RETRIEVAL':
    case 'CONSOLIDATION_STORAGE':
      return 'consolidation_flow';
    case 'EXTERNAL':
      return 'external_flow';
    case 'GOODS_TO_PERSONS':
      return undefined;
    default:
      assertNever(flowType);
  }
};

export const buildFlow = (
  type: Flow['type'],
  sourceAreaOrFlowStopId: string,
  destinationAreaOrFlowStopId: string,
  name: string,
): Flow | undefined => {
  switch (type) {
    case 'TRANSPORT': {
      return flowFactory.buildFlow({
        flowType: 'TRANSPORT',
        creationParams: {
          name,
          sourceAreaOrFlowStopId,
          destinationAreaOrFlowStopId,
        },
      });
    }
    case 'CONSOLIDATION_STORAGE':
      return flowFactory.buildFlow({
        flowType: 'CONSOLIDATION_STORAGE',
        creationParams: {
          name,
          sourceAreaOrFlowStopId,
          destinationAreaOrFlowStopId,
        },
      });
    case 'CONSOLIDATION_RETRIEVAL':
      return flowFactory.buildFlow({
        flowType: 'CONSOLIDATION_RETRIEVAL',
        creationParams: {
          name,
          sourceAreaOrFlowStopId,
          destinationAreaOrFlowStopId,
        },
      });

    case 'GOODS_TO_PERSONS':
      return flowFactory.buildFlow({
        flowType: 'GOODS_TO_PERSONS',
        creationParams: {
          name,
          sourceAreaOrFlowStopId,
          destinationAreaOrFlowStopId,
        },
      });
    case 'EXTERNAL':
      return flowFactory.buildFlow({
        flowType: 'EXTERNAL',
        creationParams: {
          name,
          sourceAreaOrFlowStopId,
          destinationAreaOrFlowStopId,
        },
      });
    default:
      logger.error(`Can not provide a default flow for flow type: ${type}`);
      return undefined;
  }
};

export function getPaletteByLoadHandling(
  loadHandlingType:
    | SingularStationArea['loadHandlingType']
    | RackingArea['rackingLoadHandlingType']
    | ManualArea['loadHandlingType']
    | undefined,
): string {
  switch (loadHandlingType) {
    case 'INTAKE':
      return theme.palette.areas.intake;
    case 'DELIVERY':
      return theme.palette.areas.delivery;
    case 'STORAGE':
      return theme.palette.areas.storage;
    case 'OFF_VEHICLE_PROCESSING':
      return theme.palette.areas.process;
    case 'ON_VEHICLE_PROCESSING':
      return theme.palette.areas.process;
    case 'INTAKE_AND_DELIVERY':
      return theme.palette.areas.manual;
    case undefined:
      return theme.palette.neutral.grey;
    default:
      assertNever(loadHandlingType);
  }
}

export const getFlowStopDetails = (
  areaOrFlowStopId: string,
): { name: string; loadHandlingType?: AreaLoadHandlingType } => {
  const { getFlowStopById, getAreaById, getAreasByIds } = useFloorplanDesignStore.getState();
  const baseAreaOrFlowStopId = getEndpointBaseId(areaOrFlowStopId);

  const flowStop = getFlowStopById(areaOrFlowStopId);
  if (flowStop) {
    // Load handling type is shared across the flow stop's areas; pick any.
    const referenceArea = areasToFlatCollection(getAreasByIds(flowStop.areaIds))[0];
    if (isLoadHandlingArea(referenceArea)) {
      const loadHandlingType = getAreaLoadHandlingType(referenceArea);

      return { name: flowStop.name, loadHandlingType };
    }
    return { name: flowStop.name };
  }

  if (baseAreaOrFlowStopId !== areaOrFlowStopId) {
    const baseFlowStop = getFlowStopById(baseAreaOrFlowStopId);
    if (baseFlowStop) {
      const referenceArea = areasToFlatCollection(getAreasByIds(baseFlowStop.areaIds))[0];
      if (isLoadHandlingArea(referenceArea)) {
        const loadHandlingType = getAreaLoadHandlingType(referenceArea);
        return { name: baseFlowStop.name, loadHandlingType };
      }
      return { name: baseFlowStop.name };
    }
  }

  const area =
    getAreaById(areaOrFlowStopId) ||
    (baseAreaOrFlowStopId !== areaOrFlowStopId ? getAreaById(baseAreaOrFlowStopId) : undefined);
  if (!area) return { name: '' };

  if (isLoadHandlingArea(area)) {
    const loadHandlingType = getAreaLoadHandlingType(area);
    return { name: area.name, loadHandlingType };
  }

  return { name: area.name };
};

export type FlowEndDetails = {
  /** Null when the flows don't share this end: shows 'Mixed' */
  name: string | null;
  /** Undefined when the ends don't share a load handling type: shows gray */
  loadHandlingType?: AreaLoadHandlingType;
};

/** @description The shared end of the flows: its name and, also for different ends, a shared load handling type */
export const getFlowEndDetails = (areaOrFlowStopIds: string[]): FlowEndDetails => {
  const ends = areaOrFlowStopIds.map(getFlowStopDetails);
  const loadHandlingTypes = ends.flatMap(({ loadHandlingType }) => loadHandlingType ?? []);

  return {
    name: getCommonString(ends.map(({ name }) => name)),
    // Every end needs a load handling type: one without is gray
    loadHandlingType:
      loadHandlingTypes.length === ends.length
        ? getCommonOption(loadHandlingTypes) ?? undefined
        : undefined,
  };
};

export const getFlowLineColor = (flowType: Flow['type']): string => {
  switch (flowType) {
    case 'TRANSPORT':
      return TRANSPORT_FLOW_LINE_COLOR;
    case 'CONSOLIDATION_STORAGE':
    case 'CONSOLIDATION_RETRIEVAL':
      return CONSOLIDATION_FLOW_LINE_COLOR;
    case 'GOODS_TO_PERSONS':
      return GOODS_TO_PERSONS_FLOW_LINE_COLOR;
    case 'EXTERNAL':
      return EXTERNAL_FLOW_LINE_COLOR;
    default:
      assertNever(flowType);
  }
};

export const getFlowTooltipKey = (type: Flow['type']): string => {
  switch (type) {
    case 'TRANSPORT':
      return 'interface:properties.flows.flow_types.transport_flow';
    case 'CONSOLIDATION_STORAGE':
    case 'CONSOLIDATION_RETRIEVAL':
      return 'interface:properties.flows.flow_types.consolidation_flow';
    case 'GOODS_TO_PERSONS':
      return 'interface:properties.flows.flow_types.goods_to_persons_flow';
    case 'EXTERNAL':
      return 'interface:properties.flows.flow_types.external_flow';
    default:
      assertNever(type);
  }
};

/**
 * @description: Group flows by type
 */
export const groupFlowsByType = (flows: Flow[]): Map<Flow['type'], string[]> => {
  const flowTypeMap: Map<Flow['type'], string[]> = new Map();

  flows.forEach((flow) => {
    if (!flowTypeMap.has(flow.type)) {
      flowTypeMap.set(flow.type, []);
    }
    const flowIds = flowTypeMap.get(flow.type);
    if (flowIds) flowIds.push(flow.id);
  });

  return flowTypeMap;
};

export const isOutOfRange = (value: number, minValue: number, maxValue: number): boolean =>
  value < minValue || value > maxValue;
